import { servicePost } from "./client.ts";
import { batchedServicePost } from "./readBatch.ts";

/* ── Pricing rule types ──────────────────────────────────── */

export type DayOfWeek = "Mon" | "Tue" | "Wed" | "Thu" | "Fri" | "Sat" | "Sun";

/**
 * How the adjusted price is rounded to look "nice":
 *  - none    → no rounding (e.g. 12.50)
 *  - nearest → nearest whole number (12.50 → 13)
 *  - down    → round down to whole (12.50 → 12)
 *  - ending  → nearest price ending in `roundingEnding` (12.50 → 12.99 / 12.49 …)
 */
export type RoundingMode = "none" | "nearest" | "down" | "ending";

export interface PricingRule {
  id: string;
  days: DayOfWeek[];
  startTime: string;
  endTime: string;
  percentage: number;
  roundingMode: RoundingMode;
  /** Fractional ending (0–0.99) used only when roundingMode === "ending". */
  roundingEnding: number;
}

/** Mirrors the Kiosk_Time_Based_Pricing table columns exactly. */
export interface LocationPricingRuleDto {
  RuleId: number;
  LocationId: number;
  StartTime: string;
  EndTime: string;
  PricePct: number;
  Mon: boolean;
  Tue: boolean;
  Wed: boolean;
  Thu: boolean;
  Fri: boolean;
  Sat: boolean;
  Sun: boolean;
  RoundingMode: string;
  RoundingEnding: number;
}

/* ── Location types ──────────────────────────────────────── */

export interface KioskLocation {
  LocationID: number;
  StoreName: string;
  StoreNumber: string;
  ShortLocationName: string;
  Address: string;
  AddressTwo: string;
  ZipCode: string;
  City: string;
  Suburb: string;
  County: string;
  Country: string;
  District: string;
  Chain: string;
  TimeZone: string;
  SegmentID: number;
  AccessLevelID: number;
  Merchandiser: number;
  FootTraffic: string;
  GLN: string;
  GSOne: string;
  RegName: string;
  OrgNumber: string;
  StorePhoneNumber: string;
  OpeningHours: string;
  CustomerNumber: string;
  Longitude: string;
  Latitude: string;
  dLocationInstallDate: string | null;
  LocationRemovalDate: string | null;
  Note: string;
  ManagerOne: string;
  PhoneOne: string;
  EmailOne: string;
  ManagerTwo: string;
  PhoneTwo: string;
  EmailTwo: string;
  Commission: number;
  AdminCost: number;
  Percent: boolean;
  QuarterCommission: boolean;
  Account: string;
  OwnerOperatorID: number;
  TS: number;
}

export interface DVDMerchandiser {
  MerchandiserID: number;
  Name: string;
}

/* ── API functions ───────────────────────────────────────── */

export function getKioskLocation(locationID: number) {
  return batchedServicePost<KioskLocation>("WebGetKioskLocation", { locationID });
}

export function saveKioskLocation(location: KioskLocation, timezone: string) {
  return servicePost<number>("WebSaveKioskLocation", { location, timezone });
}

export function getDVDMerchandisers() {
  return batchedServicePost<DVDMerchandiser[]>("WebGetDVDMerchandisers", {});
}

export function getLocationPricingRules(locationId: number) {
  return servicePost<LocationPricingRuleDto[]>("WebGetLocationPricingRules", { locationId });
}

export function saveLocationPricingRules(locationId: number, dtos: LocationPricingRuleDto[]) {
  return servicePost<boolean>("WebSaveLocationPricingRules", { locationId, rules: dtos });
}

/* ── Kiosk language (location-level `Language` kiosk setting) ── */

/**
 * The location's kiosk language. Stored as a location-level override of the
 * kiosk setting `Language` (DVD_KioskSettings, iKioskID = 0), which kiosks
 * only read when the database defines `Language` as a location setting —
 * `Level` says which: "LOCATION" (editable here) or "KIOSK" (set per kiosk in
 * Settings → Kiosk / Location). `null` Level = the setting is not defined.
 */
export interface LocationKioskLanguage {
  Level: "LOCATION" | "KIOSK" | null;
  /** The location's own override; null = uses the default. */
  Value: string | null;
  /** DVD_Settings default for `Language`. */
  DefaultValue: string | null;
  /** S_Admin_Localization_Language values (e.g. "ENG"), in iLanguageID order. */
  Languages: string[];
}

/** `locationId` 0 = a location being created (level, default and languages only). */
export function getLocationKioskLanguage(locationId: number) {
  return batchedServicePost<LocationKioskLanguage>("WebGetLocationKioskLanguage", { locationId });
}

/** Sets the location's kiosk language; "" removes the override (back to the default). */
export function saveLocationKioskLanguage(locationId: number, language: string) {
  return servicePost<boolean>("WebSaveLocationKioskLanguage", { locationId, language });
}
